import { useState } from 'react';
import { Download, Filter } from 'lucide-react';
import { useApi } from '../hooks/useApi.js';

interface AuditEntry {
  id: string;
  timestamp: string;
  user_id: string;
  user_email: string;
  device_id: string;
  product: string;
  ip_address: string;
  effect: string;
  matched_policy_id: string | null;
  matched_policy_name: string | null;
  trust_level: string;
  trust_score: number;
  require_mfa: boolean;
  read_only: boolean;
  message: string;
}

const EFFECT_STYLES: Record<string, { bg: string; text: string }> = {
  allow: { bg: 'bg-green-100', text: 'text-green-700' },
  block: { bg: 'bg-red-100', text: 'text-red-700' },
  limit: { bg: 'bg-yellow-100', text: 'text-yellow-700' },
  step_up_auth: { bg: 'bg-blue-100', text: 'text-blue-700' },
};

const TRUST_STYLES: Record<string, { bg: string; text: string }> = {
  trusted: { bg: 'bg-green-50', text: 'text-green-700' },
  conditional: { bg: 'bg-yellow-50', text: 'text-yellow-700' },
  untrusted: { bg: 'bg-red-50', text: 'text-red-700' },
};

export function AccessAuditPage() {
  const [effectFilter, setEffectFilter] = useState('');
  const queryParams = effectFilter ? `?effect=${effectFilter}&limit=100` : '?limit=100';
  const { data, loading, error } = useApi<{ data: AuditEntry[] }>(`/protect/conditional-access/audit${queryParams}`);

  const entries = data?.data ?? [];

  const handleExport = () => {
    const csv = [
      'Timestamp,User,Device,Product,Effect,Policy,Trust Level,Trust Score,IP Address',
      ...entries.map(e =>
        `${e.timestamp},${e.user_email},${e.device_id},${e.product},${e.effect},${e.matched_policy_name ?? 'none'},${e.trust_level},${e.trust_score},${e.ip_address}`
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `access-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;
  if (error) return <div className="text-red-600">{error}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Access Audit Log</h2>
          <p className="text-sm text-gray-500 mt-1">Policy evaluation history for compliance reporting</p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <Filter className="h-4 w-4 text-gray-400" />
        <select
          value={effectFilter}
          onChange={(e) => setEffectFilter(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">All effects</option>
          <option value="allow">Allow</option>
          <option value="block">Block</option>
          <option value="limit">Limit</option>
          <option value="step_up_auth">Step-Up Auth</option>
        </select>
        <span className="text-sm text-gray-500">{entries.length} entries</span>
      </div>

      {/* Audit Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Effect</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Policy</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trust</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {entries.map(entry => {
              const effectStyle = EFFECT_STYLES[entry.effect] ?? { bg: 'bg-gray-100', text: 'text-gray-700' };
              const trustStyle = TRUST_STYLES[entry.trust_level] ?? { bg: 'bg-gray-50', text: 'text-gray-700' };
              return (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                    {new Date(entry.timestamp).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{entry.user_email}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{entry.device_id ? entry.device_id.slice(0, 8) + '...' : '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{entry.product || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${effectStyle.bg} ${effectStyle.text}`}>
                      {entry.effect}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{entry.matched_policy_name ?? '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${trustStyle.bg} ${trustStyle.text}`}>
                      {entry.trust_level} ({entry.trust_score})
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 font-mono">{entry.ip_address || '-'}</td>
                </tr>
              );
            })}
            {entries.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-gray-500 text-sm">No audit entries found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
